import React, { useState, useContext, useEffect } from 'react';
import { Button, IconButton, Tooltip } from '@mui/material';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ArrowRightAltRoundedIcon from '@mui/icons-material/ArrowRightAltRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import FormLabel from 'src/components/UI/input/CustomFormLabel';
import SelectInput from 'src/components/UI/input/SelectInput';
import { organizeFormGroups } from 'src/components/form-builder/CreateQuestionnaire/components/Preview/Preview';
import { FormData, EditMode } from '../../CreateQuestionnaire';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { SnackContext } from 'src/store/ContextStore';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import styles from './Mapping.module.css';

// Currently assuming destination fields for only consent form
const destinationFields = [
  { label: 'Response', value: 'response' },
  { label: 'Consent Given By', value: 'consentGivenBy' },
  { label: 'Consent Note', value: 'consentNote' }
];
const clinicalRecordOptions = [{ label: 'Consent', value: 'consent' }];
const DEFAULT_FORM_TYPE = 'consent';

const MappingUI = ({ data, mappingsRef, formTypeRef }) => {
  const t = useAutoTranslation();

  const [formType, setFormType] = useState(DEFAULT_FORM_TYPE);
  const [mappings, setMappings] = useState([]);
  const [sourceFields, setSourcefields] = useState([]);
  const { setSnack } = React.useContext(SnackContext);
  const edit = useContext(EditMode);

  useEffect(() => {
    if (mappingsRef) mappingsRef.current = mappings;
  }, [mappings, mappingsRef]);

  useEffect(() => {
    if (formTypeRef) formTypeRef.current = formType;
  }, [formType, formTypeRef]);

  const FORM_DATA = useContext(FormData);

  useEffect(() => {
    let formData;
    if (Object.keys(FORM_DATA).length > 0) {
      formData = FORM_DATA[0];
    } else {
      formData = data.config;
    }

    const groups = organizeFormGroups(formData);

    let sourceFields = [];
    groups.map((group) => {
      group.map((form) => {
        const { formElements } = form.data;

        formElements.map((element) => {
          sourceFields.push({
            label: element.label,
            value: element.id
          });
        });
      });
    });

    // Fields for currently selected forms
    setSourcefields(sourceFields);
  }, [FORM_DATA]);

  const fetchMappings = async (schemaId) => {
    if (!schemaId) return;
    try {
      // Fetch all mappings for currently selected form.
      const resp = await axios.get(
        APICONSTANTS.formBuilderSchemasMappings.replace(':_id', schemaId)
      );

      let mappings = resp.data.mappings[formType] || [];

      setMappings(mappings);
    } catch (error) {
      console.error('Error fetching mappings:', error);
    }
  };

  useEffect(() => {
    fetchMappings(edit.schemaId);
  }, [edit]);

  const isAddMappingDisabled = () => {
    return mappings.some((mapping) => !mapping.source || !mapping.destination);
  };

  const isSaveConfigDisabled = () => {
    return mappings.some((mapping) => !mapping.source || !mapping.destination);
  };

  const getAvailableOptions = (field, currentIndex, allOptions) => {
    const usedValues = mappings
      .filter((_, i) => i !== currentIndex)
      .map((m) => m[field])
      .filter(Boolean);
    return allOptions.filter((opt) => !usedValues.includes(opt.value));
  };

  const handleAddMapping = () => {
    setMappings([...mappings, { source: '', destination: '' }]);
  };

  const handleRemoveMapping = (index) => {
    const newMappings = [...mappings];
    newMappings.splice(index, 1);
    setMappings(newMappings);
  };

  const handleChange = (index, field, value) => {
    const newMappings = [...mappings];
    newMappings[index][field] = value;
    setMappings(newMappings);
  };

  const handleSaveConfig = () => {
    if (!edit.schemaId) return;

    axios
      .post(
        APICONSTANTS.formBuilderSchemasMappings.replace(':_id', edit.schemaId),
        {
          [formType]: mappings
        }
      )
      .then((res) => {
        setSnack({
          status: true,
          msg: t('Mappings saved successfully'),
          severity: 'success'
        });
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: t('An error has occurred!'),
          severity: 'error'
        });
      });
  };

  const hasMappings = mappings && mappings.length > 0;

  return (
    <div className={styles.page}>
      {/* <div className={styles.banner}>
        <InfoOutlinedIcon fontSize="small" />
        <span>
          {t(
            'Map this form’s fields to clinical record fields so submitted responses sync to the patient record.'
          )}
        </span>
      </div> */}

      <div className={styles.layout}>
        <aside className={styles.configPanel}>
          <div className={styles.cardHeader}>
            <h2 className={styles.cardTitle}>{t('Mapping settings')}</h2>
            <p className={styles.cardSubtitle}>
              {t('Where these responses are written.')}
            </p>
          </div>
          <FormLabel className={styles.fieldLabel}>
            {t('Clinical record mapping type')}
          </FormLabel>
          <SelectInput
            fullWidth
            variant="outlined"
            options={clinicalRecordOptions}
            value={formType}
            onChange={(e) => setFormType(e.target.value)}
          />
        </aside>

        <div className={styles.mappingPanel}>
          <div className={`${styles.cardHeader} ${styles.cardHeaderRow}`}>
            <div>
              <h2 className={styles.cardTitle}>{t('Field mappings')}</h2>
              <p className={styles.cardSubtitle}>
                {t(
                  'Connect a source column from this form to a destination field on the clinical record.'
                )}
              </p>
            </div>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              startIcon={<AddRoundedIcon />}
              onClick={handleAddMapping}
              disabled={isAddMappingDisabled()}
            >
              {t('Add mapping')}
            </Button>
          </div>

          {hasMappings ? (
            <>
              <div className={styles.mappingHeaderRow}>
                <span className={styles.colLabel}>{t('Source column')}</span>
                <span />
                <span className={styles.colLabel}>
                  {t('Destination field')}
                </span>
                <span />
              </div>

              {mappings.map((mapping, index) => (
                <div className={styles.mappingRow} key={index}>
                  <SelectInput
                    fullWidth
                    variant="outlined"
                    options={getAvailableOptions('source', index, sourceFields)}
                    value={mapping.source}
                    onChange={(e) =>
                      handleChange(index, 'source', e.target.value)
                    }
                  />
                  <span className={styles.arrowCell}>
                    <ArrowRightAltRoundedIcon />
                  </span>
                  <SelectInput
                    fullWidth
                    variant="outlined"
                    options={getAvailableOptions(
                      'destination',
                      index,
                      destinationFields
                    )}
                    value={mapping.destination}
                    onChange={(e) =>
                      handleChange(index, 'destination', e.target.value)
                    }
                  />
                  <span className={styles.deleteCell}>
                    <Tooltip title={t('Remove mapping')} arrow>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleRemoveMapping(index)}
                      >
                        <DeleteOutlineRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </span>
                </div>
              ))}
            </>
          ) : (
            <div className={styles.emptyState}>
              <AccountTreeOutlinedIcon fontSize="large" />
              <span className={styles.emptyTitle}>{t('No mappings yet')}</span>
              <span className={styles.emptyHint}>
                {t('Add a mapping to link form fields to the clinical record.')}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className={styles.submitRow}>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<SaveOutlinedIcon />}
          onClick={handleSaveConfig}
          disabled={isSaveConfigDisabled()}
        >
          {t('Save configuration')}
        </Button>
      </div>
    </div>
  );
};

export default MappingUI;
